// Create-task modal, shared by the record quick-action bars (fixed target)
// and the Tasks page / TopBar QuickAdd (user picks the target record).
// Creates the ActionItem(s) in the store — which writes through to the API.
//
// Ownership model (2026-07-13, designed with Chauncey):
//   - ONE accountable person per task. "Assign to" is a live WPI-staff
//     typeahead (Graph); the durable key is ownerEmail. Smart default:
//     the target account's WPI relationship lead (first wpiTeam member
//     with an email on the account's tracks), else the creator.
//   - Account-level tasks may name related properties: ONE commitment,
//     surfaced on each linked property's Action Items tab.
//   - Fan-out is the exception: "separate task per property" creates
//     independent per-site tasks (same assignee) for site-level work.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useMsal } from '@azure/msal-react'
import { Modal } from './Modal'
import { Field, TextInput, Select } from './Field'
import { Button } from './Primitives'
import { useStore } from '@/store/useStore'
import { useCurrentUser } from '@/auth/useCurrentUser'
import { searchPeople, EMAIL_RE, type PersonHit } from '@/auth/peopleSearch'
import { signedInAccount } from '@/auth/ssoMemory'
import { todayIso } from '@/lib/format'
import type { ActionItem, Dataset, EntityRef, Priority } from '@/types/entities'

const PRIORITIES: Priority[] = ['Critical', 'High', 'Medium', 'Low']

const newId = () =>
  `ai-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`

interface Assignee { name: string; email?: string }

/** The account's WPI relationship lead — first track team member with an email. */
function accountLead(d: Dataset, accountId: string | undefined): Assignee | null {
  if (!accountId) return null
  for (const t of d.tracks) {
    if (t.accountId !== accountId) continue
    const lead = t.wpiTeam.find((m) => m.email)
    if (lead) return { name: lead.name, email: lead.email }
  }
  return null
}

export function TaskModal({
  open, onClose, attachTo, contextLabel, onCreated,
}: {
  open: boolean
  onClose: () => void
  /** Fixed target (record pages). Omit to let the user pick account/property. */
  attachTo?: EntityRef
  contextLabel?: string
  onCreated?: (items: ActionItem[]) => void
}) {
  const data = useStore((s) => s.data)
  const upsert = useStore((s) => s.upsert)
  const user = useCurrentUser()
  const { instance, accounts: msalAccounts } = useMsal()

  const [title, setTitle] = useState('')
  const [priority, setPriority] = useState<Priority>('Medium')
  const [dueDate, setDueDate] = useState('')
  const [targetKey, setTargetKey] = useState('')
  const [assignee, setAssignee] = useState<Assignee | null>(null)
  const [assigneeQuery, setAssigneeQuery] = useState('')
  const [hits, setHits] = useState<PersonHit[]>([])
  const [searchError, setSearchError] = useState<string | null>(null)
  const [relatedIds, setRelatedIds] = useState<string[]>([])
  const [fanOut, setFanOut] = useState(false)
  const searchTimer = useRef<ReturnType<typeof setTimeout>>()

  // The account whose properties can be linked (and whose lead is the
  // default assignee): the fixed target, its owner, or the picked target.
  const targetAccountId = useMemo(() => {
    if (attachTo?.kind === 'account') return attachTo.id
    if (attachTo?.kind === 'property') {
      return data.properties.find((p) => p.id === attachTo.id)?.ownerAccountId
    }
    if (targetKey.startsWith('account:')) return targetKey.slice('account:'.length)
    return undefined
  }, [attachTo, targetKey, data])

  useEffect(() => {
    if (!open) return
    setTitle(''); setPriority('Medium'); setDueDate(''); setTargetKey('')
    setAssigneeQuery(''); setHits([]); setRelatedIds([]); setFanOut(false)
    setAssignee(null) // resolved to the smart default below once the target is known
  }, [open])

  // Smart default assignee whenever the target account resolves and the
  // user hasn't picked anyone.
  useEffect(() => {
    if (!open || assigneeQuery) return
    setAssignee((cur) => {
      if (cur && (cur as Assignee & { picked?: boolean }).picked) return cur
      return accountLead(data, targetAccountId)
        ?? { name: user.displayName, email: user.email }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetAccountId, user.displayName])

  // Debounced Graph typeahead.
  useEffect(() => {
    if (!open) return
    clearTimeout(searchTimer.current)
    const q = assigneeQuery.trim()
    if (q.length < 2) { setHits([]); return }
    searchTimer.current = setTimeout(() => {
      // The ACTIVE account, never simply the first cached (auth/ssoMemory.ts).
      const account = signedInAccount(instance, msalAccounts)
      if (!account) return
      searchPeople(instance, account, q)
        .then((h) => { setHits(h); setSearchError(null) })
        .catch((err) => {
          // NEVER fail silently — an empty dropdown looks like "no such
          // person" when the real problem is auth/network.
          console.error('[TaskModal] directory search failed:', err)
          setHits([])
          setSearchError('Directory search unavailable — you can still type a full email address below.')
        })
    }, 300)
    return () => clearTimeout(searchTimer.current)
  }, [assigneeQuery, open, instance, msalAccounts])

  const targets = useMemo(() => {
    if (attachTo) return []
    const accounts = data.accounts
      .filter((a) => a.type !== 'Personal')
      .map((a) => ({ key: `account:${a.id}`, label: a.name, group: 'Accounts' }))
    const properties = [...data.properties]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((p) => ({ key: `property:${p.id}`, label: p.name, group: 'Properties' }))
    return [...accounts, ...properties]
  }, [attachTo, data])

  const accountProperties = useMemo(() => {
    if (!targetAccountId) return []
    // Only offered for account-scoped tasks (a property task IS its scope).
    if (attachTo && attachTo.kind !== 'account') return []
    if (!attachTo && !targetKey.startsWith('account:')) return []
    return data.properties
      .filter((p) => p.ownerAccountId === targetAccountId)
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [attachTo, targetKey, targetAccountId, data])

  const resolveRef = (): EntityRef | undefined => {
    if (attachTo) return attachTo
    if (!targetKey) return undefined
    const [kind, id] = targetKey.split(/:(.+)/)
    const label = kind === 'account'
      ? data.accounts.find((a) => a.id === id)?.name
      : data.properties.find((p) => p.id === id)?.name
    return { kind: kind as EntityRef['kind'], id, label }
  }

  const canSave = title.trim().length > 0 && (attachTo != null || targetKey !== '') && assignee != null

  const save = () => {
    const ref = resolveRef()
    if (!ref || !title.trim() || !assignee) return
    const common = {
      title: title.trim(),
      priority,
      status: 'Open' as const,
      owner: assignee.name,
      ownerEmail: assignee.email,
      dueDate: dueDate || undefined,
      source: `CRM · added by ${user.displayName}`,
      createdAt: todayIso(),
    }
    const created: ActionItem[] = []
    if (fanOut && relatedIds.length > 0) {
      // Independent per-site work: one task per property, each standalone.
      for (const pid of relatedIds) {
        const prop = data.properties.find((p) => p.id === pid)
        created.push({
          ...common,
          id: newId(),
          attachedTo: { kind: 'property', id: pid, label: prop?.name },
        })
      }
    } else {
      created.push({
        ...common,
        id: newId(),
        attachedTo: ref,
        relatedPropertyIds: relatedIds.length ? relatedIds : undefined,
      })
    }
    for (const item of created) upsert('actionItems', item)
    onCreated?.(created)
    onClose()
  }

  const toggleRelated = (pid: string) =>
    setRelatedIds((cur) => (cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid]))

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={<>Task{contextLabel ? <> — {contextLabel}</> : null}</>}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!canSave} onClick={save}>
            {fanOut && relatedIds.length > 1 ? `Create ${relatedIds.length} tasks` : 'Create task'}
          </Button>
        </>
      }
    >
      <Field label="Title" required>
        <TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs doing?" autoFocus />
      </Field>

      {!attachTo && (
        <Field label="Attach to" required hint="The account or property this task belongs to.">
          <Select value={targetKey} onChange={(e) => { setTargetKey(e.target.value); setRelatedIds([]) }}>
            <option value="">Choose a record…</option>
            <optgroup label="Accounts">
              {targets.filter((t) => t.group === 'Accounts').map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </optgroup>
            <optgroup label="Properties">
              {targets.filter((t) => t.group === 'Properties').map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </optgroup>
          </Select>
        </Field>
      )}

      <Field label="Assign to" required hint="One accountable person. Search WPI staff by name or email.">
        <div className="tkm-assign">
          {assignee && !assigneeQuery && (
            <button type="button" className="tkm-assignee" onClick={() => { setAssigneeQuery(assignee.name); setAssignee(null) }}>
              <strong>{assignee.name}</strong>
              {assignee.email && <span className="dim"> {assignee.email}</span>}
              <span className="tkm-assignee-edit">change</span>
            </button>
          )}
          {(!assignee || assigneeQuery) && (
            <>
              <TextInput
                value={assigneeQuery}
                onChange={(e) => setAssigneeQuery(e.target.value)}
                placeholder="Type a name…"
              />
              {searchError && <span className="field-hint" style={{ color: 'var(--warn, #C9A02C)' }}>{searchError}</span>}
              {(hits.length > 0 || EMAIL_RE.test(assigneeQuery.trim())) && (
                <div className="tkm-hits">
                  {hits.length === 0 && EMAIL_RE.test(assigneeQuery.trim()) && (
                    <button type="button" className="tkm-hit" onClick={() => {
                      const email = assigneeQuery.trim().toLowerCase()
                      setAssignee({ name: email.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), email, picked: true } as Assignee)
                      setAssigneeQuery(''); setHits([])
                    }}>
                      <span className="tkm-hit-name">Use “{assigneeQuery.trim()}”</span>
                      <span className="tkm-hit-sub dim">assign to this email address</span>
                    </button>
                  )}
                  {hits.map((h) => (
                    <button key={h.email} type="button" className="tkm-hit" onClick={() => {
                      setAssignee({ name: h.name, email: h.email, picked: true } as Assignee)
                      setAssigneeQuery(''); setHits([])
                    }}>
                      <span className="tkm-hit-name">{h.name}</span>
                      <span className="tkm-hit-sub dim">{h.email}{h.title ? ` · ${h.title}` : ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Field>

      {accountProperties.length > 0 && (
        /* NOT a <Field>: Field renders a <label>, and the rows below are
           labels themselves — nested labels are invalid HTML and made every
           click toggle the first checkbox + paint the text as selected. */
        <div className="field">
          <span className="field-label label">Related properties</span>
          <div className="tkm-props">
            {accountProperties.map((p) => (
              <label key={p.id} className="tkm-prop">
                <input
                  type="checkbox"
                  checked={relatedIds.includes(p.id)}
                  onChange={() => toggleRelated(p.id)}
                />
                <span>{p.name}</span>
              </label>
            ))}
          </div>
          {relatedIds.length > 1 && (
            <label className="tkm-fanout">
              <input type="checkbox" checked={fanOut} onChange={(e) => setFanOut(e.target.checked)} />
              <span>Create a separate task per property (independent site work)</span>
            </label>
          )}
          <span className="field-hint dim">
            {fanOut
              ? 'Each selected property gets its OWN independent task.'
              : 'One task, shown on each selected property.'}
          </span>
        </div>
      )}

      <Field label="Priority">
        <Select value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </Select>
      </Field>
      <Field label="Due date">
        <TextInput type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
      </Field>
    </Modal>
  )
}
